import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
/**
 * A <Custom> visual: code the assistant wrote, run where it can do no harm.
 *
 * Two frames. The outer one is a sandbox (scripts only: no same origin, so no
 * cookies, storage or app access; no top navigation, popups, forms or
 * modals) whose policy forbids frames from loading anything. The inner one
 * runs the code under a policy with no network at all (no fetch, images,
 * fonts or styles from anywhere), with WebRTC removed before the code runs
 * (no policy covers it; see BOOT). The inner frame cannot leave either: a
 * navigation to any URL is a frame load, which the outer policy blocks, so
 * code cannot carry data out in a link or a redirect.
 *
 * The code talks to the chat only by postMessage: it reports its height and
 * errors; the chat sends the theme's colours. Nothing else crosses.
 */ /** Colours the code may use, as CSS variables on its :root. */ const PALETTE = {
    '--fg': 'hsl(var(--foreground))',
    '--muted': 'hsl(var(--muted-foreground))',
    '--bg': 'hsl(var(--card))',
    '--surface': 'hsl(var(--muted))',
    '--border': 'hsl(var(--border))',
    '--accent': 'hsl(var(--primary))',
    '--good': 'var(--chart-good)',
    '--warn': 'var(--chart-warn)',
    '--bad': 'var(--chart-bad)',
    '--series': 'var(--chart-series)'
};
/** The palette resolved to plain colours in the page's current theme. */ function resolvePalette() {
    if (typeof document === 'undefined') return {};
    const probe = document.createElement('span');
    probe.style.display = 'none';
    document.body.appendChild(probe);
    const out = {};
    for (const [name, expr] of Object.entries(PALETTE)){
        probe.style.color = '';
        probe.style.color = expr;
        const c = getComputedStyle(probe).color;
        if (c) out[name] = c;
    }
    probe.remove();
    return out;
}
// One policy on both frames. The inner frame inherits the outer's policy as
// well as its own, so the outer must allow the code's inline scripts; its
// frame-src 'none' is what stops the inner frame navigating anywhere
// (checked in Chromium: without it, a link click reached the network).
const POLICY = "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; " + "img-src data: blob:; font-src data:; media-src data: blob:; frame-src 'none'; " + "form-action 'none'; base-uri 'none'";
// Runs before the code. First it removes WebRTC: no CSP directive covers it
// (Chromium ignores webrtc 'block'), and a peer connection reaches any
// STUN/TURN server, which carries data out. Checked in Chromium: before, the
// code's RTCPeerConnection reached a local STUN listener; after, it throws.
// The globals are fixed as undefined and non-configurable so the code cannot
// put them back, and a child frame's copy is out of reach (the code's own
// frames are cross-origin to it). Then: reports size and errors, applies
// theme updates.
const BOOT = `(function(){
Object.getOwnPropertyNames(window).forEach(function(k){if(/^(webkit)?RTC/.test(k)){
try{Object.defineProperty(window,k,{value:undefined,writable:false,configurable:false})}catch(e){}}});
var post=function(m){m.specra='custom';try{parent.parent.postMessage(m,'*')}catch(e){}};
var size=function(){post({type:'size',height:Math.ceil(document.documentElement.scrollHeight)})};
if(typeof ResizeObserver!=='undefined'){new ResizeObserver(size).observe(document.documentElement)}
addEventListener('load',size);
addEventListener('error',function(e){post({type:'error',message:String(e&&e.message||'error').slice(0,200)})});
addEventListener('message',function(e){var d=e.data;if(!d||d.specra!=='theme'||!d.vars)return;
for(var k in d.vars){if(/^--[a-z-]+$/.test(k))document.documentElement.style.setProperty(k,String(d.vars[k]))}});
})();`;
const escapeAttr = (s)=>s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
function vars(palette) {
    return Object.entries(palette).map(([k, v])=>`${k}:${v};`).join('');
}
/** The outer frame's document, holding the inner frame with the code. */ export function customFrameDocument(code, palette) {
    const inner = '<!doctype html><html><head><meta charset="utf-8">' + `<meta http-equiv="Content-Security-Policy" content="${POLICY}">` + `<style>:root{${vars(palette)}--font:system-ui,-apple-system,"Segoe UI",sans-serif;color-scheme:light dark}` + '*{box-sizing:border-box}html,body{margin:0;background:transparent;color:var(--fg);' + 'font:14px/1.45 var(--font)}</style>' + `<script>${BOOT}</script></head><body>${code}</body></html>`;
    return '<!doctype html><html><head><meta charset="utf-8">' + `<meta http-equiv="Content-Security-Policy" content="${POLICY}">` + '<style>html,body{margin:0;height:100%;overflow:hidden;background:transparent}' + 'iframe{border:0;width:100%;height:100%;display:block}</style></head><body>' + `<iframe sandbox="allow-scripts" srcdoc="${escapeAttr(inner)}"></iframe></body></html>`;
}
/**
 * The frame running the code, once it exists. The outer frame is
 * cross-origin, where reading a frame index that is not there yet throws
 * (Chromium), so check its length first.
 */ function codeWindow(frame) {
    try {
        const outer = frame?.contentWindow;
        return outer && outer.length > 0 ? outer.frames[0] : null;
    } catch  {
        return null;
    }
}
export function CustomFrame({ code, title, description, height, maxHeight = 720 }) {
    const ref = React.useRef(null);
    const [palette, setPalette] = React.useState(()=>resolvePalette());
    const [shown, setShown] = React.useState(height);
    const [error, setError] = React.useState(null);
    // The document bakes in the first palette; a later theme change is sent,
    // so switching light/dark never reloads (and resets) the illustration.
    const firstPalette = React.useRef(palette);
    const doc = React.useMemo(()=>customFrameDocument(code, firstPalette.current), [
        code
    ]);
    React.useEffect(()=>{
        const onMessage = (e)=>{
            const inner = codeWindow(ref.current);
            if (!inner || e.source !== inner) return;
            const d = e.data;
            if (!d || d.specra !== 'custom') return;
            if (d.type === 'size' && typeof d.height === 'number' && d.height > 0) {
                setShown(Math.min(maxHeight, Math.max(120, Math.ceil(d.height))));
            } else if (d.type === 'error') {
                setError(String(d.message ?? 'error'));
            }
        };
        window.addEventListener('message', onMessage);
        return ()=>window.removeEventListener('message', onMessage);
    }, [
        maxHeight
    ]);
    // Follow the app's light/dark switch.
    React.useEffect(()=>{
        if (typeof MutationObserver === 'undefined') return;
        const mo = new MutationObserver(()=>setPalette(resolvePalette()));
        mo.observe(document.documentElement, {
            attributes: true,
            attributeFilter: [
                'class',
                'data-theme',
                'style'
            ]
        });
        return ()=>mo.disconnect();
    }, []);
    React.useEffect(()=>{
        codeWindow(ref.current)?.postMessage({
            specra: 'theme',
            vars: palette
        }, '*');
    }, [
        palette
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("iframe", {
                ref: ref,
                title: title,
                "aria-label": description,
                sandbox: "allow-scripts",
                referrerPolicy: "no-referrer",
                loading: "lazy",
                srcDoc: doc,
                className: "block w-full rounded-md border border-border bg-transparent",
                style: {
                    height: shown
                }
            }),
            error && /*#__PURE__*/ _jsxs("p", {
                className: "mt-1 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: [
                    "The illustration stopped with an error: ",
                    error
                ]
            })
        ]
    });
}
